'use client'

import { useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useDeleteCampaign } from '@/core/hooks/useCampaign'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

interface RemoveCampaignModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	campaignId?: number
	campaignName?: string
}

export function RemoveCampaignModal({
	open,
	onOpenChange,
	campaignId,
	campaignName,
}: RemoveCampaignModalProps) {
	const { t } = useTranslation('campaigns')
	const queryClient = useQueryClient()
	const { showNotification } = useNotificationContext()
	const [isRemoving, setIsRemoving] = useState(false)

	const { mutateAsync: deleteCampaign } = useDeleteCampaign({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['campaigns'] })
			},
		},
	})

	const handleRemove = async () => {
		if (!campaignId) return

		setIsRemoving(true)

		try {
			await deleteCampaign(campaignId)

			showNotification({
				type: 'success',
				message: t('modals.remove.success'),
				description: t('modals.remove.successDesc', { name: campaignName }),
			})

			onOpenChange(false)
		} catch {
			showNotification({
				type: 'error',
				message: t('modals.remove.error'),
				description: t('modals.remove.errorDesc'),
			})
		} finally {
			setIsRemoving(false)
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md p-0 gap-0">
				<DialogHeader className="px-6 pt-6">
					<DialogTitle className="text-2xl font-semibold">
						{t('modals.remove.title')}
					</DialogTitle>
				</DialogHeader>

				<div className="px-6 py-2">
					<p className="text-base text-foreground">
						{t('modals.remove.confirmMessage')}
						<span className="font-semibold"> {campaignName}</span>
						{t('modals.remove.irreversible')}
					</p>
				</div>

				<Separator />

				<DialogFooter className="px-6 py-4 flex flex-row justify-end gap-3">
					<Button
						type="button"
						variant="outline"
						onClick={() => onOpenChange(false)}
						className="h-10 rounded-full px-6"
						disabled={isRemoving}
					>
						{t('modals.remove.cancel')}
					</Button>
					<Button
						type="button"
						variant="destructive"
						onClick={handleRemove}
						disabled={isRemoving}
						className="h-10 rounded-full px-6"
					>
						{isRemoving
							? t('modals.remove.removing')
							: t('modals.remove.delete')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
